import React, { useCallback, useEffect, useRef, useState } from 'react';

export type ChatRole = 'user' | 'assistant' | 'system';
export interface ChatMessage {
    id: string;
    role: ChatRole;
    content: string;
}

export interface AliroChatWidgetProps {
    onSend: (payload: {
        message: string;
        history: { role: ChatRole; content: string }[];
    }) => Promise<string | ChatMessage | ChatMessage[]>; // Updated type
    title?: string; // header title
    placeholder?: string; // input placeholder
    welcomeText?: string; // initial assistant message
    poweredBy?: string; // override footer text
    initialMessages?: ChatMessage[]; // preload messages
    zIndex?: number; // set custom z-index if needed
}

const uid = () => Math.random().toString(36).slice(2, 10);

const Dot = ({ delay }: { delay: string }) => (
    <span
        className={`inline-block h-1.5 w-1.5 rounded-full bg-current opacity-70 animate-bounce`}
        style={{ animationDelay: delay }}
    />
);

function classNames(...xs: (string | false | undefined)[]) {
    return xs.filter(Boolean).join(' ');
}

export default function AliroChatWidget({
    onSend,
    title = 'AI Assistant',
    placeholder = 'Type a message. . .',
    welcomeText = 'Hi! How can I help today?',
    poweredBy = 'powered by aliro ai',
    initialMessages = [],
    zIndex = 50,
}: AliroChatWidgetProps) {
    const [open, setOpen] = useState<boolean>(true);
    const [messages, setMessages] = useState<ChatMessage[]>(() => {
        if (initialMessages.length) return initialMessages;
        return [{ id: uid(), role: 'assistant', content: welcomeText }];
    });
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [messages, loading]);

    // Auto-resize textarea
    const resizeTA = useCallback(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = '0px';
        ta.style.height = Math.min(140, ta.scrollHeight) + 'px';
    }, []);
    useEffect(() => resizeTA(), [input, resizeTA]);

    const send = useCallback(async () => {
        const text = input.trim();
        if (!text || loading) return;
        setInput('');

        const userMsg: ChatMessage = { id: uid(), role: 'user', content: text };
        setMessages((m) => [...m, userMsg]);
        setLoading(true);

        try {
            // Send the full conversation history to the backend
            const result = await onSend({
                message: text,
                history: messages.map((msg) => ({ role: msg.role, content: msg.content })), // Send role and content only
            });

            const normalize = (r: string | ChatMessage | ChatMessage[]): ChatMessage[] => {
                if (typeof r === 'string') return [{ id: uid(), role: 'assistant', content: r }];
                if (Array.isArray(r)) return r.map((x) => ({ ...x, id: x.id ?? uid() }));
                return [{ id: r.id ?? uid(), role: r.role ?? 'assistant', content: r.content }];
            };

            const toAdd = normalize(result).map((x) => ({ ...x, role: x.role ?? 'assistant' }));
            setMessages((m) => [...m, ...toAdd]);
        } catch {
            setMessages((m) => [
                ...m,
                {
                    id: uid(),
                    role: 'assistant',
                    content: 'Sorry, I ran into an error. Please try again.',
                },
            ]);
            // optional: console.error(err)
        } finally {
            setLoading(false);
            textareaRef.current?.focus();
        }
    }, [input, loading, onSend, messages]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void send();
        }
    };

    // Message bubble
    const Bubble = useCallback(({ msg }: { msg: ChatMessage }) => {
        const isUser = msg.role === 'user';
        return (
            <div
                className={classNames(
                    'flex w-full gap-2',
                    isUser ? 'justify-end' : 'justify-start'
                )}
            >
                <div
                    className={classNames(
                        'max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm',
                        isUser
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white/80 dark:bg-zinc-800/80 border border-zinc-200/60 dark:border-zinc-700/60 text-zinc-900 dark:text-zinc-100'
                    )}
                    style={{
                        whiteSpace: 'pre-wrap', // Preserve line breaks and spacing
                        wordBreak: 'break-word', // Break long words if necessary
                    }}
                >
                    {msg.content}
                </div>
            </div>
        );
    }, []);

    return (
        <div
            className="pointer-events-none fixed bottom-4 right-4"
            style={{ zIndex }}
            aria-live="polite"
        >
            {/* FAB toggle */}
            <button
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? 'Close assistant' : 'Open assistant'}
                className={classNames(
                    'pointer-events-auto inline-flex h-12 w-12 items-center justify-center rounded-full',
                    'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30 ring-1 ring-white/20',
                    'hover:scale-105 transition-transform'
                )}
            >
                {open ? (
                    // Close icon
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="h-6 w-6"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18 18 6M6 6l12 12"
                        />
                    </svg>
                ) : (
                    // Assistant icon
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="h-6 w-6"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h6" />
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M20 12c0 4.418-4.03 8-9 8-1.033 0-2.027-.143-2.953-.41L4 20l.75-3C4.27 15.84 4 14.948 4 14c0-4.418 4.03-8 9-8s7 3.582 7 6z"
                        />
                    </svg>
                )}
            </button>

            {/* Panel */}
            <div
                className={classNames(
                    'pointer-events-auto mt-3 w-[22rem] sm:w-[24rem]',
                    'rounded-2xl border border-zinc-200/60 bg-zinc-50/90 backdrop-blur-md',
                    'dark:bg-zinc-900/80 dark:border-zinc-700/60',
                    'shadow-2xl',
                    open ? 'block' : 'hidden'
                )}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                        <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden />
                        <h3 className="text-sm font-semibold tracking-wide text-zinc-900 dark:text-zinc-100">
                            {title}
                        </h3>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                        {poweredBy}
                    </span>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-zinc-200/80 to-transparent dark:via-zinc-700/60" />

                {/* Messages */}
                <div
                    ref={listRef}
                    className="max-h-[24rem] overflow-y-auto no-scrollbar p-3 space-y-2"
                >
                    {messages.map((m) => (
                        <Bubble key={m.id} msg={m} />
                    ))}

                    {/* Loading bubble */}
                    {loading && (
                        <div className="flex w-full justify-start gap-2">
                            <div className="max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm bg-white/80 dark:bg-zinc-800/80 border border-zinc-200/60 dark:border-zinc-700/60 text-zinc-900 dark:text-zinc-100">
                                <div className="flex items-center gap-1">
                                    <Dot delay="0ms" />
                                    <Dot delay="150ms" />
                                    <Dot delay="300ms" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Composer */}
                <div className="border-t border-zinc-200/60 dark:border-zinc-700/60 px-3 py-2">
                    <div className="flex items-end gap-2">
                        <textarea
                            ref={textareaRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            className="flex-1 resize-none overflow-hidden rounded-xl border border-zinc-300/60 dark:border-zinc-700/60 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                            rows={1}
                            maxLength={4000}
                        />
                        <button
                            onClick={() => void send()}
                            disabled={!input.trim() || loading}
                            className={classNames(
                                'inline-flex h-9 w-9 items-center justify-center rounded-xl',
                                'bg-indigo-600 text-white shadow-md ring-1 ring-white/20',
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                                'hover:scale-[1.02] transition-transform'
                            )}
                            aria-label="Send message"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                className="h-5 w-5"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 12h14M12 5l7 7-7 7"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
